import React, { useMemo } from "react";
import { DsButton } from "../../../../design-system";
import {
  FIELD_LABELS,
  CONFIG_KEYS,
  consolidateComponentHistory,
} from "./utils/exportStructureComponentConfigHistory";

function formatSnapValue(key, val) {
  if (val == null || val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (key === "selectedFormula" && typeof val === "object") {
    return val.name || val.expression || "Formula";
  }
  if (key === "statusEffectiveFrom") return String(val).slice(0, 7);
  return String(val);
}

/**
 * Popup for component configuration history (one row per effective month).
 */
export default function ComponentConfigHistoryModal({
  open,
  component,
  onClose,
  onRestore,
  onDelete,
  busy = false,
}) {
  const history = useMemo(() => {
    if (!component) return [];
    return consolidateComponentHistory(component.configHistory).sort((a, b) =>
      String(b.effectiveMonth).localeCompare(String(a.effectiveMonth))
    );
  }, [component]);

  if (!open || !component) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]">
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <div>
            <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">Configuration history</h3>
            <p className="ds-caption mt-0.5 text-[hsl(var(--ds-muted-fg))]">
              {component.name || "Component"} — one version per effective month
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {!history.length ? (
            <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
              No saved versions yet. Set <strong>Status from</strong>, then use{" "}
              <strong>Save</strong> on the component.
            </p>
          ) : (
            <ul className="space-y-3">
              {history.map((h, idx) => {
                const isLatest = idx === 0;
                const changedSet = new Set(h.changedFields || []);
                return (
                  <li
                    key={h.id || h.effectiveMonth}
                    className="rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-bg))] px-3 py-3"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="ds-body font-medium text-[hsl(var(--ds-fg))]">
                          From {h.effectiveMonth}
                          {isLatest ? (
                            <span className="ml-2 text-[10px] font-semibold text-[hsl(var(--ds-primary))]">
                              Active
                            </span>
                          ) : null}
                        </p>
                        {h.changedAt ? (
                          <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                            Saved {new Date(h.changedAt).toLocaleString()}
                          </p>
                        ) : null}
                        {h.note ? (
                          <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">{h.note}</p>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <DsButton
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={() => onRestore?.(h.id)}
                        >
                          Restore
                        </DsButton>
                        <DsButton
                          type="button"
                          variant="danger"
                          size="sm"
                          disabled={busy || !isLatest}
                          title={
                            isLatest
                              ? "Delete and restore previous effective month"
                              : "Only the latest effective month can be deleted"
                          }
                          onClick={() => onDelete?.(h.id)}
                        >
                          Delete
                        </DsButton>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-[hsl(var(--ds-border))] pt-2 sm:grid-cols-3">
                      {CONFIG_KEYS.map((k) => {
                        const highlighted = changedSet.has(FIELD_LABELS[k]);
                        return (
                          <div key={k} className="min-w-0 text-[11px]">
                            <span className="text-[hsl(var(--ds-muted-fg))]">{FIELD_LABELS[k]}: </span>
                            <span
                              className={
                                highlighted
                                  ? "font-medium text-[hsl(var(--ds-warning))]"
                                  : "text-[hsl(var(--ds-fg))]"
                              }
                            >
                              {formatSnapValue(k, h.snapshot?.[k])}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex justify-end border-t border-[hsl(var(--ds-border))] px-4 py-3">
          <DsButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Close
          </DsButton>
        </div>
      </div>
    </div>
  );
}
